import {IUserPermissionFlags} from "./UserApiTypes"
import {translateUi} from "../../i18n/translateUi"


const API_PERMISSION_TO_FLAG: Record<string, keyof IUserPermissionFlags> = {
    SYSTEM_READ: "use_system",
    USE_SYSTEM: "use_system",
    USER_READ: "read_user",
    READ_USER: "read_user",
    USER_WRITE: "write_user",
    WRITE_USER: "write_user",
    BRANCH_READ: "read_branch",
    READ_BRANCH: "read_branch",
    BRANCH_WRITE: "write_branch",
    WRITE_BRANCH: "write_branch",
    BRANCH_MANAGE: "manage_branch",
    MANAGE_BRANCH: "manage_branch",
    MENU_READ: "read_menu",
    READ_MENU: "read_menu",
    MENU_WRITE: "write_menu",
    WRITE_MENU: "write_menu",
    COURIER_READ: "read_courier",
    READ_COURIER: "read_courier",
    COURIER_WRITE: "write_courier",
    WRITE_COURIER: "write_courier",
    REVIEW_READ: "read_review",
    READ_REVIEW: "read_review",
    REVIEW_WRITE: "write_review",
    WRITE_REVIEW: "write_review",
    ORDER_READ: "read_order",
    READ_ORDER: "read_order",
    ORDER_WRITE: "write_order",
    WRITE_ORDER: "write_order",
    PROMOTION_READ: "read_promotion",
    PROMOTION_WRITE: "write_promotion"
}

const FLAG_TO_API_PERMISSION: Partial<Record<keyof IUserPermissionFlags, string>> = {
    use_system: "SYSTEM_READ",
    read_user: "USER_READ",
    write_user: "USER_WRITE",
    read_branch: "BRANCH_READ",
    write_branch: "BRANCH_WRITE",
    manage_branch: "BRANCH_MANAGE",
    read_menu: "MENU_READ",
    write_menu: "MENU_WRITE",
    read_courier: "COURIER_READ",
    write_courier: "COURIER_WRITE",
    read_review: "REVIEW_READ",
    write_review: "REVIEW_WRITE",
    read_order: "ORDER_READ",
    write_order: "ORDER_WRITE",
    read_promotion: "PROMOTION_READ",
    write_promotion: "PROMOTION_WRITE"
}

const PERMISSION_META: Record<keyof IUserPermissionFlags, {label: string, description: string, capability: string, tone: string}> = {
    use_system: {
        get label() { return translateUi("Вход в админ-панель") },
        get description() { return translateUi("Разрешает сотруднику войти в административную панель.") },
        get capability() { return translateUi("Можно входить в административную панель и работать с доступными разделами.") },
        tone: "success"
    },
    read_user: {
        get label() { return translateUi("Просмотр сотрудников") },
        get description() { return translateUi("Позволяет видеть список сотрудников и открывать их карточки.") },
        get capability() { return translateUi("Можно просматривать список сотрудников и открывать их карточки.") },
        tone: "warning"
    },
    write_user: {
        get label() { return translateUi("Управление сотрудниками") },
        get description() { return translateUi("Позволяет создавать сотрудников и менять их доступы.") },
        get capability() { return translateUi("Можно создавать сотрудников, редактировать их данные и менять права доступа.") },
        tone: "processing"
    },
    read_branch: {
        get label() { return translateUi("Просмотр филиалов") },
        get description() { return translateUi("Позволяет видеть филиалы и использовать фильтры по филиалам.") },
        get capability() { return translateUi("Можно просматривать филиалы и работать с фильтрацией по филиалам.") },
        tone: "cyan"
    },
    write_branch: {
        get label() { return translateUi("Настройка филиалов") },
        get description() { return translateUi("Позволяет менять настройки филиалов, доставки и платежей.") },
        get capability() { return translateUi("Можно редактировать данные филиалов, доставку и платежные настройки.") },
        tone: "blue"
    },
    manage_branch: {
        get label() { return translateUi("Администрирование филиалов") },
        get description() { return translateUi("Позволяет выполнять служебные действия с филиалами.") },
        get capability() { return translateUi("Можно выполнять расширенное управление филиалами и синхронизации.") },
        tone: "geekblue"
    },
    read_menu: {
        get label() { return translateUi("Просмотр меню") },
        get description() { return translateUi("Позволяет смотреть меню, комбо, рекомендации, баннеры и стоп-лист.") },
        get capability() { return translateUi("Можно просматривать меню, комбо, рекомендации и связанные витринные данные.") },
        tone: "purple"
    },
    write_menu: {
        get label() { return translateUi("Управление меню") },
        get description() { return translateUi("Позволяет редактировать меню, комбо, баннеры и служебные товары.") },
        get capability() { return translateUi("Можно редактировать меню, комбо, баннеры, порядок и служебные товары.") },
        tone: "magenta"
    },
    read_courier: {
        get label() { return translateUi("Просмотр курьеров") },
        get description() { return translateUi("Позволяет видеть курьеров и их статусы.") },
        get capability() { return translateUi("Можно просматривать список курьеров, смены и статусы.") },
        tone: "lime"
    },
    write_courier: {
        get label() { return translateUi("Управление курьерами") },
        get description() { return translateUi("Позволяет создавать, редактировать и удалять курьеров.") },
        get capability() { return translateUi("Можно создавать, редактировать, блокировать и удалять курьеров.") },
        tone: "green"
    },
    read_review: {
        get label() { return translateUi("Просмотр отзывов") },
        get description() { return translateUi("Позволяет смотреть отзывы и аналитику по ним.") },
        get capability() { return translateUi("Можно просматривать отзывы, категории и детали заказов из отзывов.") },
        tone: "gold"
    },
    write_review: {
        get label() { return translateUi("Управление отзывами") },
        get description() { return translateUi("Позволяет менять настройки категорий и тегов отзывов.") },
        get capability() { return translateUi("Можно редактировать категории и теги отзывов.") },
        tone: "gold"
    },
    read_order: {
        get label() { return translateUi("Просмотр заказов") },
        get description() { return translateUi("Позволяет смотреть список и карточки заказов.") },
        get capability() { return translateUi("Можно просматривать заказы, детали доставки и состав заказа.") },
        tone: "volcano"
    },
    write_order: {
        get label() { return translateUi("Управление заказами") },
        get description() { return translateUi("Позволяет выполнять действия с заказами.") },
        get capability() { return translateUi("Можно выполнять доступные операционные действия с заказами.") },
        tone: "red"
    },
    read_promotion: {
        get label() { return translateUi("Просмотр акций") },
        get description() { return translateUi("Позволяет просматривать акции, сегменты и их списки.") },
        get capability() { return translateUi("Можно видеть акции, условия и сегменты для маркетинговых запусков.") },
        tone: "magenta"
    },
    write_promotion: {
        get label() { return translateUi("Управление акциями") },
        get description() { return translateUi("Позволяет создавать, редактировать и удалять акции и сегменты.") },
        get capability() { return translateUi("Можно изменять акции, сегменты и настраивать сегментацию.") },
        tone: "volcano"
    }
}

export const normalizePermissionKey = (permission: string) => {
    const normalized = permission.trim()
    const upper = normalized.toUpperCase()
    return API_PERMISSION_TO_FLAG[upper] || normalized
}

export const toApiPermissionKey = (permission: string) => {
    const normalized = normalizePermissionKey(permission) as keyof IUserPermissionFlags
    return FLAG_TO_API_PERMISSION[normalized] || permission
}

export const normalizePermissionFlags = (permission?: IUserPermissionFlags | string[] | null): IUserPermissionFlags => {
    if (!permission) return {}

    const flags: IUserPermissionFlags = {}
    const setFlag = (permissionKey: string, enabled = true) => {
        if (!enabled) return

        const normalized = normalizePermissionKey(permissionKey) as keyof IUserPermissionFlags
        if (FLAG_TO_API_PERMISSION[normalized]) {
            flags[normalized] = true
        }
    }

    if (Array.isArray(permission)) {
        permission.forEach((permissionKey) => {
            if (typeof permissionKey === "string") {
                setFlag(permissionKey)
            }
        })
        return flags
    }

    Object.entries(permission).forEach(([permissionKey, enabled]) => {
        setFlag(permissionKey, Boolean(enabled))
    })

    return flags
}

export const permissionFlagsToList = (permission?: IUserPermissionFlags | string[] | null) => {
    if (!permission) return [] as string[]
    return Object.entries(normalizePermissionFlags(permission))
        .filter(([, enabled]) => Boolean(enabled))
        .map(([key]) => normalizePermissionKey(key))
}

export const getUserPermissionList = (user?: {
    permission?: IUserPermissionFlags | string[] | null
    permission_list?: string[] | null
} | null) => {
    if (!user) return [] as string[]
    if (Array.isArray(user.permission_list)) return permissionFlagsToList(user.permission_list)
    return permissionFlagsToList(user.permission)
}

export const getPermissionTone = (permission: string) => {
    const normalized = normalizePermissionKey(permission) as keyof IUserPermissionFlags
    return PERMISSION_META[normalized]?.tone || "default"
}

export const formatPermissionLabel = (permission: string) => {
    const normalized = normalizePermissionKey(permission) as keyof IUserPermissionFlags
    return PERMISSION_META[normalized]?.label || permission
}

export const formatPermissionDescription = (permission: string) => {
    const normalized = normalizePermissionKey(permission) as keyof IUserPermissionFlags
    return PERMISSION_META[normalized]?.description || ""
}

export const formatPermissionCapability = (permission: string) => {
    const normalized = normalizePermissionKey(permission) as keyof IUserPermissionFlags
    return PERMISSION_META[normalized]?.capability || permission
}
